import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import {
  mdiAlertCircle,
  mdiAlertOctagon,
  mdiCheckCircle,
  mdiClockAlert,
  mdiHelpCircleOutline,
} from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { SECTION_ICONS } from '../../lib/section-icons'
import { cn } from '../../lib/utils'
import type { AssetInspection } from '../../hooks/useComplianceApi'

/**
 * Status tone of a periodic inspection — the same tokens as the Compliance
 * tab's status pills (STATUS_PILL_CONFIG), kept here so the Equipment tab does
 * not import the whole Compliance component. `order` ranks the most actionable
 * first: it picks the status a Vérifications cell shows for several
 * inspections.
 */
export const INSPECTION_STATUS: Record<
  AssetInspection['status'],
  { icon: string; color: string; cell: string; labelKey: string; order: number }
> = {
  NON_COMPLIANT: {
    icon: mdiAlertCircle,
    color: 'text-destructive',
    cell: 'border-destructive/25 bg-destructive/10 text-destructive hover:bg-destructive/20',
    labelKey: 'asset.status_non_compliant',
    order: 0,
  },
  EXPIRING: {
    icon: mdiClockAlert,
    color: 'text-warning',
    cell: 'border-warning/25 bg-warning/10 text-warning hover:bg-warning/20',
    labelKey: 'asset.status_expiring',
    order: 1,
  },
  ERROR: {
    icon: mdiAlertOctagon,
    color: 'text-warning',
    cell: 'border-warning/25 bg-warning/10 text-warning hover:bg-warning/20',
    labelKey: 'asset.status_error',
    order: 2,
  },
  UNKNOWN: {
    icon: mdiHelpCircleOutline,
    color: 'text-muted-foreground',
    cell: 'border-dashed border-muted-foreground/40 bg-muted/40 text-muted-foreground hover:bg-muted',
    labelKey: 'asset.status_unknown',
    order: 3,
  },
  COMPLIANT: {
    icon: mdiCheckCircle,
    color: 'text-success',
    cell: 'border-success/25 bg-success/10 text-success hover:bg-success/20',
    labelKey: 'asset.status_compliant',
    order: 4,
  },
}

/** The inspection a summary shows: most actionable status, then earliest due. */
export function headlineInspection(items: AssetInspection[]): AssetInspection | null {
  if (items.length === 0) return null
  return [...items].sort((a, b) => {
    const d = (INSPECTION_STATUS[a.status]?.order ?? 9) - (INSPECTION_STATUS[b.status]?.order ?? 9)
    if (d !== 0) return d
    return (a.next_due_date ?? '9999').localeCompare(b.next_due_date ?? '9999')
  })[0]
}

/** A YYYY-MM-DD date in the reader's locale, or an em dash. */
export function formatInspectionDate(value: string | undefined, locale: string): string {
  if (!value) return '—'
  const [y, m, d] = value.split('-').map(Number)
  if (!y || !m || !d) return value
  return new Date(y, m - 1, d).toLocaleDateString(locale)
}

/**
 * Periodic inspections as a list: status, rule label, last and next dates,
 * and the reports that prove them. Shared by the Equipment tab's Vérifications
 * popover and the periodic-inspections section of the Compliance tab.
 */
export function InspectionList({
  org,
  items,
  showAsset = false,
  testid,
}: {
  org: string
  items: AssetInspection[]
  /** Name the asset on each line, for a list spanning several assets. */
  showAsset?: boolean
  testid: string
}) {
  const { t, i18n } = useTranslation('compliance')
  const lang = i18n.language?.split('-')[0] || 'en'
  const sorted = [...items].sort(
    (a, b) =>
      (INSPECTION_STATUS[a.status]?.order ?? 9) - (INSPECTION_STATUS[b.status]?.order ?? 9) ||
      (a.next_due_date ?? '9999').localeCompare(b.next_due_date ?? '9999'),
  )

  return (
    <ul className="divide-y divide-border" data-testid={testid}>
      {sorted.map((item) => {
        const tone = INSPECTION_STATUS[item.status] ?? INSPECTION_STATUS.UNKNOWN
        const label = item.label?.[lang] || item.label?.en || item.rule_code || ''
        const proofs = item.evidence.filter((e) => !e.candidate)
        return (
          <li
            key={item.evaluation_uid}
            className="py-2 first:pt-0 last:pb-0"
            data-testid={`${testid}-item-${item.rule_code ?? item.rule_uid}`}
            data-status={item.status}
          >
            <div className="flex items-start gap-2">
              <span title={t(tone.labelKey)} className="mt-0.5 shrink-0">
                <AppIcon path={tone.icon} size="xs" className={tone.color} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-foreground">{label}</div>
                {showAsset && item.asset_name && (
                  <div className="text-xs text-muted-foreground truncate">{item.asset_name}</div>
                )}
                <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-muted-foreground">
                  <span data-testid={`${testid}-item-${item.rule_code ?? item.rule_uid}-last`}>
                    {t('inspections.last')} {formatInspectionDate(item.last_inspection_date, i18n.language)}
                  </span>
                  <span data-testid={`${testid}-item-${item.rule_code ?? item.rule_uid}-next`}>
                    {t('inspections.next_due')} {formatInspectionDate(item.next_due_date, i18n.language)}
                  </span>
                </div>
                {proofs.length > 0 ? (
                  <div className="mt-1 flex flex-col gap-0.5">
                    {proofs.map((e) => (
                      <Link
                        key={e.uid}
                        to="/$org/documents/$docUid"
                        params={{ org, docUid: e.document_uid }}
                        className={cn(
                          'inline-flex max-w-full items-center gap-1 text-xs hover:underline',
                          e.expired ? 'text-destructive' : 'text-primary',
                        )}
                        data-testid={`${testid}-report-${e.uid}`}
                      >
                        <AppIcon path={SECTION_ICONS.documents} size="xs" className="shrink-0" />
                        <span className="truncate">{e.document_name}</span>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="mt-1 text-xs text-muted-foreground italic">{t('inspections.no_report')}</div>
                )}
              </div>
            </div>
          </li>
        )
      })}
    </ul>
  )
}
